// @flow
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import cx from "classnames";
import glamorous from "glamorous";
import { createStructuredSelector } from "reselect";
import TabHeader from "root/components/TabHeader";
import { consts as uploaderConsts } from "uploader";
import t from "common/i18n";
import Button from "root/components/Button";
import consts from "../../data/consts";
import {
	selectPanelOpen,
	selectCurrentContext,
	selectTabData,
	selectSearchTerm,
} from "../../selectors";
import bindActions, { types as ats } from "../../actions";
import SVGs from "~/assets/icons/icons";

type Props = {
    searchTermText: string,
    isPanelOpen: boolean,
    currentContext: string,
    tabData: Object,
    actions: { [string]: Function }
};

const GHeaderContent = glamorous.div(
	cx("panel-header-container", "flex justify-content-between", "h-100"),
	{ width: "36px" },
);

export const Header = (props: Props) => {
	const {
		actions, currentContext, searchTermText, isPanelOpen,
	} = props;
	const [searchTerm, setSearchTerm] = useState(searchTermText);

	useEffect(() => {
		// reset term in response to selecting a folder from the tree
		if (searchTermText === undefined && currentContext !== consts.CONTEXTS.SEARCH) {
			setSearchTerm("");
		}
	}, [currentContext, searchTermText, actions]);

	const onSearch = useCallback(() => {
		actions[ats.SET_SEARCH_TERM]({ term: searchTerm });

		if (isPanelOpen) {
			actions[ats.TOGGLE_PANEL]();
		}
	}, [actions, isPanelOpen, searchTerm]);

	const onLogout = useCallback(() => {
		actions[ats.DO_LOGOUT]();
	}, [actions]);

	const clearSearchTerm = useCallback(() => {
		setSearchTerm("");
	}, []);

	const resetSearch = useCallback(() => {
		setSearchTerm("");

		actions[ats.CLEAR_SEARCH_TERM]();

		if (!isPanelOpen) {
			actions[ats.TOGGLE_PANEL]();
		}
	}, [actions, isPanelOpen]);

	return (
		<TabHeader
			source={uploaderConsts.SOURCES.GOOGLE_DRIVE}
			userName={props.tabData.user ? props.tabData.user.name : ""}
			searchTerm={searchTerm}
			showSearch
			expandSearch
			hideBackButton
			onLogout={onLogout}
			searchClickHandler={onSearch}
			searchTermHandler={setSearchTerm}
			clearBtnTip={t("google_drive.search.reset")}
			showClearButton={!!searchTerm}
			onClearClickHandler={clearSearchTerm}
			onEscClickHandler={resetSearch}
			searchInputPlaceholder={t("google_drive.search.placeholder")}
		>
			<GHeaderContent>
				<Button
					dataTest="google-drive-panel-trigger"
					textColorName="menu-icons"
					icon={props.isPanelOpen ? SVGs.LT_ARROW : SVGs.MENU}
					iconSize={props.isPanelOpen ? 22 : "sm"}
					fontSize={12}
					onClick={() => actions[ats.TOGGLE_PANEL]()}
				/>
			</GHeaderContent>
		</TabHeader>
	);
};

export default connect<Props, *, _, _, _, _>(
	createStructuredSelector({
		searchTermText: selectSearchTerm,
		isPanelOpen: selectPanelOpen,
		currentContext: selectCurrentContext,
		tabData: selectTabData,
	}),
	(dispatch: Function) => ({
		actions: bindActions(dispatch),
	}),
)(Header);
